import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiPhone, FiMail, FiClock, FiCalendar } = FiIcons;

const Contact = () => {
  React.useEffect(() => {
    document.title = "Contact Melissa's Hairdressing Salon - Let's Connect";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Get in touch with Melissa's Hairdressing Salon for appointments or inquiries. We're here to help you achieve your dream look!";
    }
  }, []);

  const contactInfo = [
    {
      icon: FiMapPin,
      title: "Visit Us",
      details: ["123 Beauty Street", "Style City, XY 45678"],
      link: "https://maps.google.com"
    },
    {
      icon: FiPhone,
      title: "Call Us",
      details: ["(123) 456-7890"],
      link: "tel:+11234567890"
    },
    {
      icon: FiMail,
      title: "Email Us",
      details: ["contact@melissashair.com"],
      link: "mailto:contact@melissashair.com"
    },
    {
      icon: FiClock,
      title: "Opening Hours",
      details: [
        "Mon - Fri: 9:00 AM - 7:00 PM",
        "Saturday: 9:00 AM - 6:00 PM",
        "Sunday: 10:00 AM - 5:00 PM"
      ]
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
              We are thrilled to connect with you at Melissa's Hairdressing Salon! Whether you're ready to book an appointment or have inquiries about our services, we are here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact-form"
                className="btn-primary text-white px-8 py-4 rounded-full font-medium text-lg"
              >
                <SafeIcon icon={FiCalendar} className="inline mr-2" />
                Book Your Appointment
              </Link>
              <a 
                href="tel:+11234567890"
                className="border-2 border-amber-600 text-amber-600 px-8 py-4 rounded-full font-medium text-lg hover:bg-amber-600 hover:text-white transition-all"
              >
                <SafeIcon icon={FiPhone} className="inline mr-2" />
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Cards */}
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-8"
              >
                We're Just a Call or Click Away!
              </motion.h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg card-hover"
                  >
                    <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-3 rounded-full w-12 h-12 mb-4 flex items-center justify-center">
                      <SafeIcon icon={info.icon} className="text-white text-xl" />
                    </div>
                    <h3 className="font-playfair text-lg font-semibold text-gray-900 mb-3">
                      {info.title}
                    </h3>
                    <div className="space-y-1">
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-gray-600">
                          {info.link && detailIndex === 0 ? (
                            <a 
                              href={info.link}
                              className="hover:text-amber-600 transition-colors"
                            >
                              {detail}
                            </a>
                          ) : (
                            detail
                          )}
                        </p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-gradient-to-br from-amber-100 to-rose-100 rounded-2xl p-8 flex items-center justify-center min-h-96"
            >
              <div className="text-center">
                <SafeIcon icon={FiMapPin} className="text-6xl text-amber-600 mx-auto mb-4" />
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
                  Find Us Here
                </h3>
                <p className="text-gray-600 mb-6">
                  Located in the heart of Style City, our salon is easily accessible and offers convenient parking.
                </p>
                <a 
                  href="https://maps.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-white px-6 py-3 rounded-full font-medium"
                >
                  Get Directions
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Highlight */}
      <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What We Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Feel free to reach out for personalized consultations or any questions. Our friendly team is ready to assist you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: "Custom Haircuts",
                description: "Precision cuts tailored to your face shape and style preferences."
              },
              {
                title: "Professional Coloring",
                description: "Expert color services from subtle highlights to bold transformations."
              },
              {
                title: "Special Occasion Styling",
                description: "Beautiful updos and styling for weddings, proms, and special events."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-6 rounded-2xl shadow-lg card-hover text-center"
              >
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center"
          >
            <Link 
              to="/services"
              className="btn-primary text-white px-8 py-3 rounded-full font-medium text-lg mr-4"
            >
              View All Services
            </Link>
            <Link 
              to="/contact-form"
              className="border-2 border-amber-600 text-amber-600 px-8 py-3 rounded-full font-medium text-lg hover:bg-amber-600 hover:text-white transition-all"
            >
              Book Appointment
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "How far in advance should I book my appointment?",
                answer: "We recommend booking 1-2 weeks in advance, especially for weekend appointments and special occasions. However, we often have same-day availability for certain services."
              },
              {
                question: "What should I expect during my first visit?",
                answer: "Your first visit includes a comprehensive consultation where we discuss your hair goals, lifestyle, and maintenance preferences. We'll analyze your hair type and face shape to recommend the best styles and treatments."
              },
              {
                question: "Do you offer consultations?",
                answer: "Yes! We offer complimentary consultations for all new clients. This allows us to understand your needs and create a personalized plan for achieving your desired look."
              },
              {
                question: "What is your cancellation policy?",
                answer: "We require at least 24 hours notice for cancellations or rescheduling. Late cancellations may be subject to a fee to respect our stylists' time and other clients waiting for appointments."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-gradient-to-r from-amber-50 to-rose-50 p-6 rounded-xl"
              >
                <h3 className="font-playfair text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Look?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Book your appointment and experience the difference today!
            </p>
            <Link 
              to="/contact-form"
              className="bg-white text-amber-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all inline-block"
            >
              Book Your Appointment
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;